import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ApiRequestService,
    DateUtilService,
    MAT_ICON,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService,
    Void
} from '@prekog/common-ui';
import { AddFestivalRequest } from './addfestivalrequest';
import { UpdateFestivalRequest } from './updatefestivalrequest';
import { SUCCESSFULLY_MODIFIED, SUCCESSFULLY_SAVED } from 'src/app/shared/adminconstants';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-addfestival',
    templateUrl: './addfestival.component.html',
    styleUrls: ['./addfestival.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, MatIconModule, TranslationPipe]
})
export class AddFestivalComponent implements OnInit {
    addFestival!: FormGroup;

    addFestivalUri = '/admin/addFestival';
    defaultDate!: string;

    selectedFesitvalIdForEdit: number | undefined;
    selectedMode: string | null = null;
    storeBaseUrl = inject<string>('STORE_BACKEND_URL' as never);

    updateFestivalUri = '/admin/updateFestival';

    private _fb = inject(FormBuilder);

    private _router = inject(Router);

    private _route = inject(ActivatedRoute);

    private _location = inject(Location);

    private _apiRequestService = inject(ApiRequestService);

    private _toastMessageService = inject(ToastmessageService);

    private _translateService = inject(TranslationService);

    private _dateUtilService = inject(DateUtilService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else {
            this._route.queryParamMap.subscribe((params) => {
                this.selectedMode = params.get('mode');
            });
            if (this.selectedMode === 'edit') {
                this._buildFestivalFormForEdit();
            } else {
                this._setDefaultDate();
                this._buildAddFestivalForm();
            }
        }
    }

    onCancel(): void {
        this._location.back();
    }

    onSave(): void {
        if (this.selectedMode === 'edit') {
            this._editFestival();
        } else {
            this._addFestival();
        }
    }

    private _buildAddFestivalForm(): void {
        this.addFestival = this._fb.group({
            festivalName: [null],
            comment: [null],
            festivalStartDate: [this.defaultDate],
            festivalEndDate: [this.defaultDate]
        });
    }

    private _buildFestivalFormForEdit(): void {
        const selectedFestivalForEdit = localStorage.getItem('festival');
        if (selectedFestivalForEdit) {
            const festival = JSON.parse(selectedFestivalForEdit);

            this.selectedFesitvalIdForEdit = festival.id;

            this.addFestival = this._fb.group({
                festivalName: [festival.name],
                comment: [festival.comment],
                festivalStartDate: [this._getformattedDate(festival.startDate)],
                festivalEndDate: [this._getformattedDate(festival.endDate)]
            });

            localStorage.removeItem('festival');
        }
    }

    private _setDefaultDate(): void {
        const startDate = new Date();
        this.defaultDate = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate
            .getDate()
            .toString()
            .padStart(2, '0')}`;
    }

    private _getformattedDate(date: string): string {
        const editableStartDate = new Date(this._dateUtilService.formatDate(date));
        const formattedDate = `${editableStartDate?.getFullYear()}-${(editableStartDate?.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${editableStartDate?.getDate().toString().padStart(2, '0')}`;

        return formattedDate;
    }

    private _editFestival(): void {
        const updateFestivalRequest: UpdateFestivalRequest = {
            id: this.selectedFesitvalIdForEdit,
            name: this.addFestival.value.festivalName,
            comment: this.addFestival.value.comment,
            startDate: this.addFestival.value.festivalStartDate,
            endDate: this.addFestival.value.festivalEndDate
        };

        this._apiRequestService
            .sendRequest<UpdateFestivalRequest, Void>(this.storeBaseUrl, updateFestivalRequest, true, this.updateFestivalUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_MODIFIED),
                    MAT_ICON.success
                );
                void this._router.navigateByUrl('/festivals');
            });
    }

    private _addFestival(): void {
        const addFestivalRequest: AddFestivalRequest = {
            name: this.addFestival.value.festivalName,
            comment: this.addFestival.value.comment,
            startDate: this.addFestival.value.festivalStartDate,
            endDate: this.addFestival.value.festivalEndDate
        };

        this._apiRequestService
            .sendRequest<AddFestivalRequest, Void>(this.storeBaseUrl, addFestivalRequest, true, this.addFestivalUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_SAVED),
                    MAT_ICON.success
                );
                void this._router.navigateByUrl('/festivals');
            });
    }
}
